import { useState, useEffect, useRef } from 'react'

const IDLE_TIMEOUT = 1000 * 60 * 10 // 10 minutes
// const IDLE_TIMEOUT = 1000 * 60 * 2 // 10 minutes

/**
 * Returns true when the user has been idle (no mouse, click, or keyboard activity) for 10 minutes.
 * Resets to false on any user interaction.
 */
export function useIdleDetection(): boolean {
  const [isIdle, setIsIdle] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      setIsIdle((prev) => {
        if (prev) console.log('[useIdleDetection] Activity detected — resuming from idle')
        return false
      })
      timerRef.current = setTimeout(() => {
        console.log('[useIdleDetection] User is now idle')
        setIsIdle(true)
      }, IDLE_TIMEOUT)
    }

    const events = ['mousemove', 'click', 'keypress']
    events.forEach((event) => window.addEventListener(event, resetTimer))

    // Start the initial timer
    timerRef.current = setTimeout(() => setIsIdle(true), IDLE_TIMEOUT)

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer))
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return isIdle
}
